import {tileset} from "@frog/source/data/tilesets.js"
import Renderer from "@frog/source/utility/gl/render.js"
import * as tilespreadFormats from "@frog/source/models/dev/tilespread-formats.js"

function layerKeyFromBlock(block) {
    if(tileset[block.tileKey].layerString != undefined) return "layer: " + tileset[block.tileKey].layerString + ", z:" + block.position.z
    return "z:" + block.position.z
}

const placeholderColors = {}
const TILESIZE = 96
export default new class Tilesetter {
    constructor() {
        this.canvasContexts = {}
    }
    drawEnvironment({world, isRevealingSecretTiles}) {
        this.canvasContexts = {}

        const tileKeys = {}
        Object.values(world.blocks).forEach((block) => {
            if(tileset[block.tileKey] == undefined) return
            if(block.tileKey == "not-found") placeholderColors[block.tilegid] = {}

            const layerKey = layerKeyFromBlock(block)
            if(this.canvasContexts[layerKey] == undefined) {
                const canvas = document.createElement("canvas")
                canvas.width = world.width * TILESIZE
                canvas.height = world.height * TILESIZE
                this.canvasContexts[layerKeyFromBlock(block)] = canvas.getContext("webgl2")
                this.canvasContexts[layerKeyFromBlock(block)].renderer = new Renderer(canvas, layerKey)
                this.canvasContexts[layerKeyFromBlock(block)].z = block.position.z
                this.canvasContexts[layerKeyFromBlock(block)].effect = tileset[block.tileKey].layerEffect
            }

            if(tileKeys[layerKey] == undefined) tileKeys[layerKey] = []
            if(tileKeys[layerKey].includes(block.tileKey) == false) tileKeys[layerKey].push(block.tileKey)
        })

        Object.keys(placeholderColors).sort().forEach((gid, index) => {
            const intensity = (250 / Object.keys(placeholderColors).length) * index
            placeholderColors[gid] = {
                "r": intensity,
                "g": intensity,
                "b": intensity,
            }
        })

        Object.keys(tileKeys).forEach((layerKey) => {
            const z = parseInt(layerKey.split("z:")[1])
            tileKeys[layerKey].sort((keyA, keyB) => tileset[keyA].drawOrder - tileset[keyB].drawOrder).forEach((tileKey) => {
                if(tileset[tileKey]?.image != undefined) {
                    this.canvasContexts[layerKey].renderer.processImageIntoTexture(tileset[tileKey].image)
                }
                if(tileset[tileKey]?.images != undefined) {
                    Object.values(tileset[tileKey].images).forEach((image) => {
                        this.canvasContexts[layerKey].renderer.processImageIntoTexture(image)
                    })
                }
                if(tileset[tileKey].isSecret == true
                && isRevealingSecretTiles != true) return
                const channelSuffix = tileset[tileKey].channelKey == undefined ? "" : "_" + tileset[tileKey].channelKey
                if(tileset[tileKey].grid == "corner") {
                    for(let x = -0.5; x < world.width + 0.5; x += 1) {
                        for(let y = -0.5; y < world.width + 0.5; y += 1) {
                            const position = {
                                "x": x,
                                "y": y,
                                "z": z,
                            }
                            drawCorner({
                                "canvas": this.canvasContexts[layerKey],
                                "position": position,
                                "tileKey": tileKey,
                                "world": world,
                                "suffix": channelSuffix,
                            })
                        }
                    }
                } else {
                    for(let x = 0; x < world.width; x += 1) {
                        for(let y = 0; y < world.height; y += 1) {
                            const block = world.blocks[x + "x" + y + "x" + z + channelSuffix]
                            if(block != undefined
                            && block.tileKey == tileKey) {
                                drawTile(this.canvasContexts[layerKey], block, world, channelSuffix)
                            }
                        }
                    }
                }
            })
        })

        return Object.keys(this.canvasContexts).sort((aKey,bKey) => {
            const a = this.canvasContexts[aKey]
            const b = this.canvasContexts[bKey]

            if(a.z < b.z) return -1
            if(a.z > b.z) return +1
            if(a.z == b.z) {
                if(a.effect == undefined && b.effect != undefined) return -1
                if(b.effect == undefined && a.effect != undefined) return +1

                if(a.effect != undefined
                && b.effect != undefined
                && a.effect != b.effect) {
                    const EFFECT_ORDERING = ["multiply", "screen", "overlay"]
                    return EFFECT_ORDERING.indexOf(a.effect) - EFFECT_ORDERING.indexOf(b.effect)
                }

                const aDraworders = tileKeys[aKey].map((tileKey) => tileset[tileKey].drawOrder)
                const bDraworders = tileKeys[bKey].map((tileKey) => tileset[tileKey].drawOrder)
                if(Math.max(aDraworders) < Math.min(bDraworders)) return -1
                if(Math.max(bDraworders) < Math.min(aDraworders)) return +1

                return -1
            }
        }).map((layerKey) => {
            const context = this.canvasContexts[layerKey]
            return {
                "url": context.canvas.toDataURL(),
                "effect": context.effect,
                "z": context.z,
            }
        })
    }
}

function fillTilesRectangle(context, color, leftCornerX,topCornerY, width,height) {
    context.renderer.render({
        "color": color,
        "bounds": {
            "x1": leftCornerX * TILESIZE,
            "y1": topCornerY * TILESIZE,
            "x2": leftCornerX * TILESIZE + width * TILESIZE,
            "y2": topCornerY * TILESIZE + height * TILESIZE,
        },
    })
}

function drawTile(canvas, tile, world, suffix) {
    if(tileset[tile.tileKey] == undefined) return

    const x = tile.position.x
    const y = tile.position.y
    const z = tile.position.z

    if(tileset[tile.tileKey].isAlternatingChecker == true && (x + y) % 2 == 0) return

    const checkOccupancy = (position) => {
        return world.blocks[position.x + "x" + position.y + "x" + z + suffix] != undefined
            && world.blocks[position.x + "x" + position.y + "x" + z + suffix].tileKey == tile.tileKey
    }

    tile.neighbors = {}
    tile.neighbors.hasNorth = checkOccupancy({"x": x, "y": y - 1})
    tile.neighbors.hasEast = checkOccupancy({"x": x + 1, "y": y})
    tile.neighbors.hasSouth = checkOccupancy({"x": x, "y": y + 1})
    tile.neighbors.hasWest = checkOccupancy({"x": x - 1, "y": y})
    tile.wangEdgeIndex = 0
    if(tile.neighbors.hasNorth) tile.wangEdgeIndex += 1
    if(tile.neighbors.hasEast) tile.wangEdgeIndex += 2
    if(tile.neighbors.hasSouth) tile.wangEdgeIndex += 4
    if(tile.neighbors.hasWest) tile.wangEdgeIndex += 8

    canvas.globalCompositeOperation = tileset[tile.tileKey].compositeOperation || "source-over"
    if(tileset[tile.tileKey].tilespreadType == "edge-match") {
        const tilesize = tileset[tile.tileKey].tileSize
        const sourceRectangle = {}
        const destinationRectangle = {}

        sourceRectangle.x = tilespreadFormats.wangEdgeMatch[tile.wangEdgeIndex].x * tilesize
        sourceRectangle.y = tilespreadFormats.wangEdgeMatch[tile.wangEdgeIndex].y * tilesize
        sourceRectangle.w = tilesize
        sourceRectangle.h = tilesize
        destinationRectangle.x = tile.position.x * TILESIZE
        destinationRectangle.y = tile.position.y * TILESIZE
        destinationRectangle.w = TILESIZE
        destinationRectangle.h = TILESIZE

        if(tileset[tile.tileKey].image != undefined) {
            canvas.renderer.drawImage({
                "image": tileset[tile.tileKey].image,
                "colors": tile.colors || tileset[tile.tileKey].gradientSwap,
                "source": sourceRectangle,
                "destination": destinationRectangle,
            })
        } else if(tileset[tile.tileKey].images != undefined) {
            canvas.renderer.drawImage({
                "image": tileset[tile.tileKey].images[tile.wangEdgeIndex + ""],
                "colors": tile.colors || tileset[tile.tileKey].gradientSwap,
                "destination": destinationRectangle,
            })
        }
    }
    if(tileset[tile.tileKey].tilespreadType == "standalone") {
        canvas.globalCompositeOperation = "source-over"
        canvas.renderer.drawImage({
            "image": tileset[tile.tileKey].image,
            "colors": tile.colors || tileset[tile.tileKey].gradientSwap,
            "destination": {
                "x": tile.position.x * TILESIZE,
                "y": tile.position.y * TILESIZE,
                "w": TILESIZE,
                "h": TILESIZE,
            },
        })
    }
    if(tileset[tile.tileKey].tilespreadType == undefined
    && tileset[tile.tileKey].tint != undefined) {
        let color = tileset[tile.tileKey].tint
        if(tile.colors != undefined) {
            color = tile.colors[0]
        }
        if(tile.tileKey == "not-found") color = placeholderColors[tile.tilegid]
        if(tileset[tile.tileKey].checkerColor != undefined
        && (tile.position.x + tile.position.y) % 2 == 0) {
            color = tileset[tile.tileKey].checkerColor
            if(tile.colors != undefined) {
                if(tile.colors[1] != undefined) {
                    color = tile.colors[1]
                } else {
                    color = tile.colors[0]
                }
            }
        }
        fillTilesRectangle(canvas,
            [color.r / 255, color.g / 255, color.b / 255],
            tile.position.x, tile.position.y, 1, 1)
    }
    if(tileset[tile.tileKey].isGridded == true) {
        if(tile.neighbors.hasWest == true) {
            //drawRoughTileLine(canvas, tile.position.x, tile.position.y, tile.position.x, tile.position.y + 1)
        }
        if(tile.neighbors.hasNorth == true) {
            //drawRoughTileLine(canvas, tile.position.x, tile.position.y, tile.position.x + 1, tile.position.y)
        }
    }
}

function drawCorner({canvas, position, tileKey, world, suffix}) {
    const tilesize = tileset[tileKey].tileSize
    const x = position.x
    const y = position.y
    const z = position.z || 0

    const checkOccupancy = (inputPosition) => {
        return world.blocks[inputPosition.x + "x" + inputPosition.y + "x" + z + suffix] != undefined
            && world.blocks[inputPosition.x + "x" + inputPosition.y + "x" + z + suffix].tileKey == tileKey
    }

    neighbors = {}
    neighbors.hasNorthWest = checkOccupancy({"x": x - 0.5, "y": y - 0.5})
    neighbors.hasNorthEast = checkOccupancy({"x": x + 0.5, "y": y - 0.5})
    neighbors.hasSouthWest = checkOccupancy({"x": x - 0.5, "y": y + 0.5})
    neighbors.hasSouthEast = checkOccupancy({"x": x + 0.5, "y": y + 0.5})
    let wangCornerIndex = 0
    if(neighbors.hasNorthWest) wangCornerIndex += 1
    if(neighbors.hasNorthEast) wangCornerIndex += 2
    if(neighbors.hasSouthEast) wangCornerIndex += 4
    if(neighbors.hasSouthWest) wangCornerIndex += 8
    if(wangCornerIndex == 0) return

    let colors = tileset[tileKey].gradientSwap
    const neighboringPositions = [
        {"x": x - 0.5, "y": y - 0.5},
        {"x": x + 0.5, "y": y - 0.5},
        {"x": x - 0.5, "y": y + 0.5},
        {"x": x + 0.5, "y": y + 0.5},
    ]
    for(let neighborPosition of neighboringPositions) {
        const neighborBlock = world.blocks[neighborPosition.x + "x" + neighborPosition.y + "x" + z + suffix]
        if(neighborBlock?.tileKey == tileKey && neighborBlock?.colors != undefined) colors = neighborBlock.colors
    }

    canvas.globalCompositeOperation = tileset[tileKey].compositeOperation || "source-over"
    if(tileset[tileKey].image != undefined) {
        if(tileset[tileKey].tilespreadType == "corner-match") {
            canvas.renderer.drawImage({
                "image": tileset[tileKey].image,
                "colors": colors,
                "source": {
                    "x": tilespreadFormats.wangCornerMatch[wangCornerIndex].x * tilesize,
                    "y": tilespreadFormats.wangCornerMatch[wangCornerIndex].y * tilesize,
                    "w": tilesize,
                    "h": tilesize,
                },
                "destination": {
                    "x": x * TILESIZE,
                    "y": y * TILESIZE,
                    "w": TILESIZE,
                    "h": TILESIZE,
                },
            })
        }
        if(tileset[tileKey].tilespreadType == "corner-continuous") {
            canvas.renderer.drawImage({
                "image": tileset[tileKey].image,
                "colors": colors,
                "source": {
                    "x": tilespreadFormats.wangCornerContinuous[wangCornerIndex].x * tilesize,
                    "y": tilespreadFormats.wangCornerContinuous[wangCornerIndex].y * tilesize,
                    "w": tilesize,
                    "h": tilesize,
                },
                "destination": {
                    "x": x * TILESIZE,
                    "y": y * TILESIZE,
                    "w": TILESIZE,
                    "h": TILESIZE,
                },
            })
        }
    } else if(tileset[tileKey].images != undefined) {
        canvas.renderer.drawImage({
            "image": tileset[tileKey].images[wangCornerIndex + ""],
            "colors": colors,
            "destination": {
                "x": x * TILESIZE,
                "y": y * TILESIZE,
                "w": TILESIZE,
                "h": TILESIZE,
            },
        })
    }
}
